import type { ComponentProps } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useUnmount } from "app/custom-hooks/useUnmount";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import companyAnalysisTrackingService from "app/pages/sales-intelligence/modules/company-analysis/services/tracking/companyAnalysisTrackingService";
import {
    contactsTableFiltersChangedAction,
    contactsTableSortByChangedAction,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/action-creators";
import {
    selectContactsCorrectCurrentPage,
    selectContactsPageSize,
} from "app/pages/sales-intelligence/modules/company-analysis/store/contacts/selectors";
import ContactsResultsTableContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsResultsTable/ContactsResultsTableContainer";
import { createContactsResultsTableColumns } from "app/pages/sales-intelligence/sub-modules/contacts-search/configuration/columns/createContactsResultsTableColumns";
import { useContactsFlowStrategy } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import { exportSelectedContactsToExcelForCompanyThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import {
    selectContactsResultsAreBeingFetched,
    selectCorrectTotalCount,
    selectQuotaIsBeingFetched,
    selectSearchResultsFetchError,
    selectSearchResultsWithRevealedInfo,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import type { ContactIdCompanyPair } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";

import { AVAILABLE_COMPANY_ANALYSIS_CONTACT_COLUMNS } from "../../../constants/availableColumns";

import { StyledContactsResultsTableWrapper } from "./styles";

type Props = Pick<ComponentProps<typeof ContactsResultsTableContainer>, "onRefetchData">;

const ContactsTableContainer = ({ onRefetchData }: Props) => {
    const dispatch = useDispatch();

    const contactsFlowStrategy = useContactsFlowStrategy();

    const { params } = useCurrentStateAndParamsWithFixedDomain();

    // Table page & count
    const totalCount = useSelector(selectCorrectTotalCount);
    const currentPage = useSelector(selectContactsCorrectCurrentPage);
    const pageSize = useSelector(selectContactsPageSize);
    // Search results
    const searchResults = useSelector(selectSearchResultsWithRevealedInfo);
    const isLoadingContactsResults = useSelector(selectContactsResultsAreBeingFetched);
    const searchResultsFetchError = useSelector(selectSearchResultsFetchError);

    // Contacts access
    const isQuotaBeingFetched = useSelector(selectQuotaIsBeingFetched);

    const isLoading = isQuotaBeingFetched || isLoadingContactsResults;

    useUnmount(() => {
        dispatch(contactsTableFiltersChangedAction({ page: 1 }));
    });

    const handlePageChange = (pageNumber: number) => {
        if (currentPage === 2 && pageNumber === 3) {
            companyAnalysisTrackingService.contacts.trackContactsResultsPageIncreasedTwice();
        }

        if (pageNumber !== currentPage) {
            dispatch(contactsTableFiltersChangedAction({ page: pageNumber }));
        }
    };

    const handleSortingChange = (orderBy: string, asc: boolean) => {
        void dispatch(contactsTableSortByChangedAction({ orderBy, asc }));
    };

    const handleExcelExportSubmit = async (contactIdCompanyPair: ContactIdCompanyPair) => {
        await dispatch(
            exportSelectedContactsToExcelForCompanyThunk(
                [contactIdCompanyPair],
                params.key,
                contactsFlowStrategy.dataSource.exportExcel,
            ),
        );
    };

    return (
        <StyledContactsResultsTableWrapper>
            <ContactsResultsTableContainer
                isShowCounter
                isLoading={isLoading}
                isError={isLoading ? false : !!searchResultsFetchError}
                error={searchResultsFetchError}
                currentPage={currentPage}
                pageSize={pageSize}
                data={searchResults.rows}
                totalCount={totalCount}
                availableColumns={AVAILABLE_COMPANY_ANALYSIS_CONTACT_COLUMNS}
                onSortingChange={handleSortingChange}
                onPageChange={handlePageChange}
                onRefetchData={onRefetchData}
                onExportItemToExel={handleExcelExportSubmit}
                createColumnsConfigFactory={createContactsResultsTableColumns}
            />
        </StyledContactsResultsTableWrapper>
    );
};

export default ContactsTableContainer;
